import {
  BarController,
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Plugin,
  TimeScale,
  Title,
  Tooltip,
  VisualElement
} from 'chart.js';
import { Tooltip as MuiTooltip } from '@mui/material';
import 'chartjs-adapter-date-fns';
import dayjs from 'dayjs';
import { memo, useMemo, useRef, useState } from 'react';
import { Bar, ChartProps } from 'react-chartjs-2';
import {
  CapacityBand,
  CAPACITY_BAND_COLOURS,
  CAPACITY_BAND_LABELS,
  CAPACITY_BANDS,
  DeliveryContainerCapacityRunPoint
} from '../../../../../../features/reports/delivery-container-utilisation/delivery-container-capacity-props.map';
import { AreaChartDateLabelUnit } from '../../../charts/area/area-chart.component';
import { LoadingIndicator } from '../../../loading-indicator/loading-indicator.component';
import { useDeliveryContainerCapacityChartStyles } from './delivery-container-capacity-chart.styles';

ChartJS.register(TimeScale, CategoryScale, LinearScale, BarController, BarElement, Title, Tooltip, Legend);

// Chart.js's built-in positioners place the tooltip at `element.tooltipPosition()`, and
// only ArcElement overrides that to return a midpoint — every other element type returns
// its raw x. For a horizontal floating bar that x is the run's END, so the caret always
// sat over the right-hand edge of the range. BarElement.getCenterPoint() does know the
// midpoint between the bar's base and its end, so position from that instead.
declare module 'chart.js' {
  interface TooltipPositionerMap {
    capacityRunCentre: TooltipPositionerFunction<ChartType>;
  }
}

Tooltip.positioners.capacityRunCentre = function capacityRunCentre(items) {
  // ActiveElement types `element` as the base Element, which has no geometry helpers;
  // getCenterPoint comes from VisualElement, which every drawn element implements.
  const element = items[0]?.element as (typeof items)[number]['element'] & VisualElement;
  if (!element) {
    return false;
  }
  const { x, y } = element.getCenterPoint();
  // A run can start before the visible window or end after it, which puts its midpoint
  // off-canvas. Clamp so the caret stays over the part of the bar the user can see.
  const { left, right } = this.chart.chartArea;
  return { x: Math.min(Math.max(x, left), right), y };
};

export interface DeliveryContainerCapacityChartProps {
  isLoading: boolean;
  data: ChartData<'bar'>;
  groupNames: string[];
  fromDate?: Date;
  toDate: Date;
  dateLabelUnit: AreaChartDateLabelUnit;
  /**
   * When true, the tooltip drops the date prefix from start/end times for runs that sit
   * within a single calendar day. The page sets this when the overall range is "1 day" or
   * "1 week".
   */
  compactTimeFormat?: boolean;
}

function formatRunTime(start: number, end: number, compactTimeFormat: boolean): string {
  // Show the date whenever the two timestamps fall on different days, rather than whenever
  // the run lasts 24h+ — a short run can still straddle midnight, and "10:00pm – 2:00am"
  // is ambiguous without it.
  //
  // Compares the values as printed, including `end` being exclusive (the start of the
  // bucket where the band changed). So a run ending at midnight is dated even though it
  // doesn't reach into the next day: what's on screen still reads as the next date, and a
  // full-day run would otherwise render as a bare "12:00am – 12:00am".
  const spansDays = !dayjs(start).isSame(dayjs(end), 'day');
  const useTimeOnly = compactTimeFormat && !spansDays;
  const fmt = useTimeOnly ? 'h:mma' : 'D MMM, h:mma';
  return `${dayjs(start).format(fmt)} – ${dayjs(end).format(fmt)}`;
}

const ROW_HEIGHT = 36;
// Canvas space above/below the rows: just the x-axis ticks now that the band key is
// rendered as HTML underneath rather than as a Chart.js legend inside the canvas.
const CHROME_HEIGHT = 56;
// Locked y-axis width so the plot area lines up with the heatmap above. The heatmap
// imports this so the two charts stay aligned. Over-long names are cut by CSS at the
// width the column actually has (see `rowLabelText`) rather than at a character count,
// with the full name on the label's tooltip.
export const DELIVERY_CONTAINER_UTILISATION_Y_AXIS_WIDTH = 180;

/** Layout snapshot read from the chart after each render — drives the HTML label overlay. */
interface RowMetrics {
  /** Centre-y pixel of each category, in chart-canvas coordinates */
  centres: number[];
  /** Height of one category row in pixels */
  rowHeight: number;
  /** Width of the chart-canvas region covered by the (hidden) y-axis */
  axisWidth: number;
}

export const DeliveryContainerCapacityChart = memo(function DeliveryContainerCapacityChart({
  isLoading,
  data,
  groupNames,
  fromDate,
  toDate,
  dateLabelUnit,
  compactTimeFormat = false
}: DeliveryContainerCapacityChartProps) {
  const classes = useDeliveryContainerCapacityChartStyles();

  // Row label positions are read off the chart after Chart.js lays it out, so we can render
  // HTML labels that align pixel-perfectly with the chart's category rows.
  const [rowMetrics, setRowMetrics] = useState<RowMetrics | null>(null);
  // Stable setter ref so the plugin's identity stays stable across renders.
  const setterRef = useRef(setRowMetrics);
  setterRef.current = setRowMetrics;

  const positionPlugin = useMemo<Plugin<'bar'>>(
    () => ({
      id: 'rowLabelPositions',
      afterUpdate(chart) {
        const yScale = chart.scales.y;
        const labels = chart.data.labels as string[] | undefined;
        if (!yScale || !labels || labels.length === 0) {
          return;
        }
        const centres = labels.map((_, i) => yScale.getPixelForValue(i));
        const rowHeight =
          labels.length > 1 ? Math.abs(centres[1] - centres[0]) : Math.max(yScale.bottom - yScale.top, ROW_HEIGHT);
        const axisWidth = yScale.right - yScale.left;
        const next: RowMetrics = { centres, rowHeight, axisWidth };
        setterRef.current((prev) => {
          if (
            prev &&
            prev.rowHeight === next.rowHeight &&
            prev.axisWidth === next.axisWidth &&
            prev.centres.length === next.centres.length &&
            prev.centres.every((c, i) => c === next.centres[i])
          ) {
            return prev;
          }
          return next;
        });
      }
    }),
    []
  );

  const options: ChartProps<'bar'>['options'] = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      plugins: {
        // Chart.js only ever draws a legend label beside its colour box, and the design
        // puts the label underneath. The band key is rendered as HTML below the canvas
        // instead — see the legend block in the markup.
        legend: { display: false },
        tooltip: {
          position: 'capacityRunCentre',
          callbacks: {
            title(items) {
              const raw = items[0]?.raw as DeliveryContainerCapacityRunPoint | undefined;
              return raw?.y ?? '';
            },
            label(ctx) {
              const raw = ctx.raw as DeliveryContainerCapacityRunPoint | undefined;
              if (!raw) {
                return '';
              }
              const hours = raw.hoursInRun.toFixed(1);
              const timeRange = formatRunTime(raw.x[0], raw.x[1], compactTimeFormat);
              const bandLabel = CAPACITY_BAND_LABELS[raw.band as CapacityBand] ?? raw.band;
              return [`${bandLabel} — ${hours} h`, timeRange];
            }
          }
        }
      },
      scales: {
        x: {
          type: 'time',
          min: fromDate ? fromDate.getTime() : undefined,
          max: toDate.getTime(),
          time: {
            unit: dateLabelUnit,
            displayFormats: { hour: 'ha', day: 'EEE d MMM', month: 'MMM yyyy' }
          },
          ticks: { source: 'auto', maxRotation: 0, autoSkip: true, font: { family: 'Poppins' } }
        },
        y: {
          type: 'category',
          labels: groupNames,
          stacked: false,
          // Reserve the same width as the heatmap's row-header column so chart areas line up.
          afterFit(scale) {
            scale.width = DELIVERY_CONTAINER_UTILISATION_Y_AXIS_WIDTH;
          },
          // Chart.js's own y-axis chrome is hidden — the HTML overlay below draws the labels.
          ticks: { display: false },
          grid: { display: false, drawBorder: false }
        }
      }
    }),
    [fromDate, toDate, dateLabelUnit, groupNames, compactTimeFormat]
  );

  const height = Math.max(groupNames.length, 1) * ROW_HEIGHT + CHROME_HEIGHT;

  return (
    <div className={classes.root}>
      <div className={classes.wrapper} style={{ height }}>
        {isLoading && (
          <div className={classes.loading}>
            <LoadingIndicator />
          </div>
        )}
        <Bar options={options} data={data} plugins={[positionPlugin]} />
        {rowMetrics && (
          <div className={classes.labelOverlay} style={{ width: rowMetrics.axisWidth, height }}>
            {groupNames.map((name, i) => {
              const centre = rowMetrics.centres[i];
              if (centre == null) return null;
              return (
                <MuiTooltip key={name} title={name} placement="top" classes={{ tooltip: classes.nameTooltip }}>
                  <div
                    className={classes.rowLabel}
                    style={{
                      top: centre - rowMetrics.rowHeight / 2,
                      width: rowMetrics.axisWidth,
                      height: rowMetrics.rowHeight
                    }}
                  >
                    <span className={classes.rowLabelText}>{name}</span>
                  </div>
                </MuiTooltip>
              );
            })}
          </div>
        )}
      </div>
      <div className={classes.legend}>
        {CAPACITY_BANDS.map((band) => (
          <div key={band} className={classes.legendItem}>
            <span className={classes.legendSwatch} style={{ backgroundColor: CAPACITY_BAND_COLOURS[band] }} />
            <span className={classes.legendLabel}>{CAPACITY_BAND_LABELS[band]}</span>
          </div>
        ))}
      </div>
    </div>
  );
});
